/**
 * Design thumbnail with click-to-enlarge dialog (Phase 65 / BLU-594 PR2).
 *
 * 64px preview over a checkerboard (light) or dark backdrop so transparency
 * reads honestly; opens a shadcn Dialog with the full preview + filename
 * caption. Dialog-from-row (never dialog-from-dialog, DESIGN §10). Falls back to
 * an icon tile when the image can't load (a pasted URL that 404s).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface DesignThumbProps {
  url: string;
  filename: string;
  /** Dark garment backdrop (white artwork on dark). */
  dark?: boolean;
  /** Accessible label for the trigger button, e.g. "Enlarge front light design". */
  enlargeLabel: string;
  closeLabel: string;
}

export function DesignThumb({ url, filename, dark = false, enlargeLabel, closeLabel }: DesignThumbProps) {
  const { t } = useTranslation('productSync');
  const [open, setOpen] = useState(false);
  const [thumbError, setThumbError] = useState(false);
  const [fullError, setFullError] = useState(false);

  // BLU-818 extracted the checkerboard to the shared `artwork-checker` utility
  // (index.css) so the film station's artwork tiles and this one cannot drift, and
  // so the pattern follows the theme instead of hardcoding #EDEBF2/#fff.
  const backdrop = dark ? 'bg-[#2A2733]' : 'artwork-checker';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={enlargeLabel}
        className={cn(
          'grid h-16 w-16 shrink-0 cursor-zoom-in place-items-center overflow-hidden rounded-md border',
          'transition-shadow duration-200 hover:ring-2 hover:ring-threadheads-purple/30 motion-reduce:transition-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          backdrop,
        )}
      >
        {thumbError ? (
          <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        ) : (
          <img
            src={url}
            alt=""
            className="h-full w-full object-contain"
            onError={() => setThumbError(true)}
          />
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="sr-only">{t('tier2.enlarge', { slot: filename })}</DialogTitle>
          </DialogHeader>
          <div className={cn('grid aspect-square w-full place-items-center overflow-hidden rounded-lg border', backdrop)}>
            {fullError ? (
              <ImageOff className="h-16 w-16 text-muted-foreground" aria-hidden="true" />
            ) : (
              <img
                src={url}
                alt={filename}
                className="h-full w-full object-contain"
                onError={() => setFullError(true)}
              />
            )}
          </div>
          <p className="text-center font-mono text-xs text-muted-foreground">{filename}</p>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-8 rounded-md border border-border px-3 text-xs font-medium transition-colors duration-200 hover:bg-muted motion-reduce:transition-none"
            >
              {closeLabel}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
